import { authenticationService } from "@authentication/services/AuthenticationService";
import { BaseForm } from "@forms/core/BaseForm";
import { StringField } from "@forms/core/StringField";
import { SelectField } from "@forms/core/SelectField";
import PropTypes from "prop-types";
import React, { Component } from "react";
import Overridable from "react-overridable";
import { Container, Message, Loader, Dimmer } from "semantic-ui-react";
import { schema as getProfileSchema } from "./schema";
import { uiSchema as getProfileUiSchema } from "./uiSchema";
import ProfileContainer from "./ProfileContainer";
import { userApi } from "@api/users/user";

const PROFILE_FIELDS = [
  "email",
  "username",
  "full_name",
  "contact_number",
  "department",
  "program",
  "year",
  "semester",
];

const INITIAL_PROFILE_STATE = PROFILE_FIELDS.reduce((acc, field) => {
  acc[field] = "";
  return acc;
}, {});

const INITIAL_STATE = {
  profile: INITIAL_PROFILE_STATE,
  user: {},
  originalProfile: {},
  isLoading: true,
  hasError: false,
  errorMessage: "",
  hasSuccess: false,
  successMessage: "",
  needsEmailVerification: false,
  isReadOnly: false,
};

const ERROR_MESSAGES = {
  401: {
    load: "Please log in to view your profile.",
    update: "Please log in to update your profile.",
    debug: "Status: 401 Unauthorized",
  },
  404: {
    load: "Profile endpoint not found.",
    update: "Profile update endpoint not found.",
    debug: "Status: 404 - Endpoint not found",
  },
  network: "CORS or network error. Your backend may need CORS configuration.",
  default: "An unexpected error occurred.",
};

const createSelectOptions = (enumValues, enumNames) =>
  enumValues.map((value, index) => ({
    key: value,
    value: value,
    text: enumNames[index],
  }));

const extractProfileData = (userData) => ({
  ...PROFILE_FIELDS.reduce((acc, field) => {
    acc[field] = userData[field] || "";
    return acc;
  }, {}),
  id: userData.id?.toString() || "",
  roles: Array.isArray(userData.roles) ? userData.roles.join(", ") : "",
  last_login_at: userData.last_login_at || "",
  location_pid: userData.location_pid || "",
});

const getErrorMessage = (error, context = "load") => {
  const status = error.response?.status;
  const isNetworkError =
    error.message?.includes("Network Error") || error.message?.includes("CORS");

  if (ERROR_MESSAGES[status]) {
    const { [context]: message, debug } = ERROR_MESSAGES[status];
    return `${message}\n\nDebug Info: ${debug}`;
  }

  if (isNetworkError) {
    return `${ERROR_MESSAGES.network}\n\nDebug Info: ${error.message}`;
  }

  return `${ERROR_MESSAGES.default}\n\nDebug Info: ${
    error.message || "Unknown error"
  }`;
};

const getBaseFieldConfigs = () => [
  {
    component: StringField,
    props: {
      fieldPath: "email",
      label: "Email Address",
      placeholder: "Email Address",
      type: "email",
      icon: "mail",
      iconPosition: "left",
      required: true,
    },
  },
  {
    component: StringField,
    props: {
      fieldPath: "username",
      label: "Username",
      placeholder: "Username",
      type: "text",
      icon: "user",
      iconPosition: "left",
    },
  },
  {
    component: StringField,
    props: {
      fieldPath: "full_name",
      label: "Full Name",
      placeholder: "Full Name",
      type: "text",
      icon: "user outline",
      iconPosition: "left",
    },
  },
];

const ProfileFormFields = ({ isReadOnly, profileSchema, profileUiSchema }) => {
  const baseFields = getBaseFieldConfigs();

  return (
    <>
      {baseFields.map(({ component: Component, props: fieldProps }, index) => (
        <Component key={fieldProps.fieldPath} {...fieldProps} />
      ))}

      <StringField
        fieldPath="contact_number"
        label={profileSchema.properties.contact_number.title}
        placeholder={profileUiSchema.contact_number["ui:placeholder"]}
        type="tel"
        icon={profileUiSchema.contact_number["ui:options"].icon}
        iconPosition={profileUiSchema.contact_number["ui:options"].iconPosition}
        disabled={isReadOnly}
      />

      {["department", "program", "year", "semester"].map((field) => (
        <SelectField
          key={field}
          fieldPath={field}
          label={profileSchema.properties[field].title}
          placeholder={profileUiSchema[field]["ui:placeholder"]}
          options={createSelectOptions(
            profileSchema.properties[field].enum,
            profileSchema.properties[field].enumNames
          )}
          icon={profileUiSchema[field]["ui:options"].icon}
          iconPosition={profileUiSchema[field]["ui:options"].iconPosition}
          disabled={isReadOnly}
        />
      ))}
    </>
  );
};

const MessageDisplay = ({
  hasError,
  errorMessage,
  hasSuccess,
  successMessage,
}) => (
  <>
    {hasError && (
      <Message negative>
        <Message.Header>Error Loading Profile</Message.Header>
        <div style={{ whiteSpace: "pre-line" }}>{errorMessage}</div>
      </Message>
    )}

    {hasSuccess && (
      <Message positive>
        <Message.Header>Success</Message.Header>
        <p>{successMessage}</p>
      </Message>
    )}
  </>
);

const LoadingState = () => (
  <Container>
    <Dimmer active inverted>
      <Loader inverted>Loading Profile...</Loader>
    </Dimmer>
  </Container>
);

// Main Profile Component
class Profile extends Component {
  constructor(props) {
    super(props);
    this.state = INITIAL_STATE;
    this.profileSchema = getProfileSchema();
    this.profileUiSchema = getProfileUiSchema();
  }

  componentDidMount() {
    this.loadProfile();
  }

  get buttons() {
    return this.state.isReadOnly
      ? []
      : [
          {
            name: "profile",
            content: "Update Profile",
            primary: true,
            type: "submit",
            icon: "check",
          },
        ];
  }

  updateStateWithProfileData = (profileData) => {
    const profile = extractProfileData(profileData);
    const user = { ...profileData, ...profile };

    this.setState({
      profile,
      user,
      originalProfile: profileData,
    });
  };

  setLoadingState = (isLoading, additionalState = {}) => {
    this.setState({
      isLoading,
      hasError: false,
      hasSuccess: false,
      ...additionalState,
    });
  };

  setErrorState = (error, context = "load", additionalState = {}) => {
    this.setState({
      isLoading: false,
      hasError: true,
      errorMessage: getErrorMessage(error, context),
      hasSuccess: false,
      ...additionalState,
    });
  };

  setSuccessState = (message, additionalState = {}) => {
    this.setState({
      hasSuccess: true,
      successMessage: message,
      hasError: false,
      isLoading: false,
      ...additionalState,
    });
  };

  loadProfile = async () => {
    try {
      this.setLoadingState(true);

      const response = await authenticationService.getCurrentUser();
      const profileData = response.data || response;
      const needsEmailVerification = !profileData.confirmed_at;
      const isReadOnly = this.props.readOnly || profileData.read_only || false;

      this.updateStateWithProfileData(profileData);

      this.setState({
        needsEmailVerification,
        isReadOnly,
        isLoading: false,
        hasError: false,
      });
    } catch (error) {
      this.setErrorState(error, "load");
    }
  };

  validateProfile = (values) => {
    const errors = {};

    // Email validation
    if (values.email && !/\S+@\S+\.\S+/.test(values.email)) {
      errors.email = "Please enter a valid email address";
    }

    // Required field validation
    const requiredFields = ["username", "full_name"];
    requiredFields.forEach((field) => {
      if (!values[field]?.trim()) {
        errors[field] = `${field.replace("_", " ")} is required`;
      }
    });

    // Contact number validation
    if (
      values.contact_number &&
      this.profileSchema.properties.contact_number.pattern
    ) {
      const contactPattern = new RegExp(
        this.profileSchema.properties.contact_number.pattern
      );
      if (!contactPattern.test(values.contact_number)) {
        errors.contact_number = "Please enter a valid contact number";
      }
    }

    return errors;
  };

  onSubmit = async (values, actions = {}) => {
    const safeActions = {
      setSubmitting: (submitting) => {
        if (actions.setSubmitting) {
          actions.setSubmitting(submitting);
        }
      },
      setErrors: (errors) => {
        if (actions.setErrors) {
          actions.setErrors(errors);
        } else {
          console.warn("Validation errors:", errors);
        }
      },
    };

    try {
      safeActions.setSubmitting(true);
      this.setState({ hasError: false, hasSuccess: false });

      const validationErrors = this.validateProfile(values);
      if (Object.keys(validationErrors).length > 0) {
        safeActions.setErrors(validationErrors);
        return;
      }

      const allowedFields = [
        "library_id",
        "username",
        "full_name",
        "contact_number",
        "department",
        "program",
        "year",
        "semester",
      ];

      const updateData = {};
      allowedFields.forEach((field) => {
        if (values[field] !== undefined && values[field] !== null) {
          updateData[field] = values[field];
        }
      });

      const response = await userApi.update(updateData);

      const updatedProfileData = response.data || response;

      this.updateStateWithProfileData(updatedProfileData);

      this.setState((prevState) => ({
        hasSuccess: true,
        successMessage: "Profile updated successfully! Refreshing page...",
        profile: {
          ...prevState.profile,
          ...values,
        },
      }));
    } catch (error) {
      let errorMessage = "Failed to update profile.";
      if (error.response?.status === 400) {
        // Handle validation errors specifically
        const validationErrors =
          error.response.data?.errors || error.response.data;
        if (typeof validationErrors === "object") {
          errorMessage = `Validation failed: ${Object.entries(validationErrors)
            .map(
              ([field, msg]) =>
                `${field}: ${Array.isArray(msg) ? msg.join(", ") : msg}`
            )
            .join("; ")}`;
        } else {
          errorMessage = "Invalid data provided.";
        }
      } else if (error.response?.status === 401) {
        errorMessage = "Please log in to update your profile.";
      } else if (error.response?.status === 404) {
        errorMessage = "Profile update endpoint not found.";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      this.setState({
        hasError: true,
        errorMessage,
      });
    } finally {
      safeActions.setSubmitting(false);
    }
  };

  onSendVerificationEmail = async () => {
    try {
      this.setLoadingState(true);
      // TODO: Implement actual email verification
      this.setSuccessState(
        "Email verification feature is not implemented yet."
      );
    } catch (error) {
      this.setErrorState(error, "load", {
        errorMessage: "Email verification not available yet.",
      });
    }
  };

  onCancel = () => {
    this.setState({
      profile: extractProfileData(this.state.originalProfile),
      hasError: false,
      hasSuccess: false,
    });
  };

  render() {
    const {
      profile,
      user,
      isLoading,
      hasError,
      errorMessage,
      hasSuccess,
      successMessage,
      needsEmailVerification,
      isReadOnly,
    } = this.state;

    if (isLoading) {
      return <LoadingState />;
    }

    return (
      <Overridable
        id="Profile.layout"
        profile={profile}
        buttons={this.buttons}
        onSubmit={this.onSubmit}
      >
        <ProfileContainer
          profile={profile}
          user={user}
          isLoading={isLoading}
          hasError={hasError}
          errorMessage={errorMessage}
          hasSuccess={hasSuccess}
          successMessage={successMessage}
          needsEmailVerification={needsEmailVerification}
          isReadOnly={isReadOnly}
          buttons={this.buttons}
          onSubmit={this.onSubmit}
          onSendVerificationEmail={this.onSendVerificationEmail}
          onCancel={this.onCancel}
        >
          <MessageDisplay
            hasError={hasError}
            errorMessage={errorMessage}
            hasSuccess={hasSuccess}
            successMessage={successMessage}
          />

          <BaseForm
            initialValues={profile}
            buttons={this.buttons}
            onSubmit={this.onSubmit}
            readOnly={isReadOnly}
          >
            <ProfileFormFields
              isReadOnly={isReadOnly}
              profileSchema={this.profileSchema}
              profileUiSchema={this.profileUiSchema}
            />
          </BaseForm>
        </ProfileContainer>
      </Overridable>
    );
  }
}

ProfileFormFields.propTypes = {
  isReadOnly: PropTypes.bool.isRequired,
  profileSchema: PropTypes.object.isRequired,
  profileUiSchema: PropTypes.object.isRequired,
};

MessageDisplay.propTypes = {
  hasError: PropTypes.bool,
  errorMessage: PropTypes.string,
  hasSuccess: PropTypes.bool,
  successMessage: PropTypes.string,
};

MessageDisplay.defaultProps = {
  hasError: false,
  errorMessage: "",
  hasSuccess: false,
  successMessage: "",
};

Profile.propTypes = {
  readOnly: PropTypes.bool,
};

Profile.defaultProps = {
  readOnly: false,
};

export default Overridable.component("Profile", Profile);
